import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@/ui/primitives';
import type { SxProps, Theme } from '@/ui/primitives';
import type { TooltipProps } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type OverflowTooltipProps = {
  title: string | null | undefined;
  children: ReactNode;
  wrapperSx?: SxProps<Theme>;
  inline?: boolean;
  placement?: TooltipProps['placement'];
};

export const OverflowTooltip = ({
  title,
  children,
  wrapperSx,
  inline = false,
  placement = 'bottom',
}: OverflowTooltipProps): ReactElement => {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const [overflowed, setOverflowed] = useState(false);
  const resolvedTitle = title?.trim() ?? '';

  const updateOverflowState = useCallback(() => {
    const node = containerRef.current;
    if (!node) return;
    const childNode = node.firstElementChild;
    const childElement = childNode instanceof HTMLElement ? childNode : null;
    const nextOverflowed = Boolean(
      node.scrollWidth > node.clientWidth ||
      node.scrollHeight > node.clientHeight ||
      (childElement &&
        (childElement.scrollWidth > childElement.clientWidth ||
          childElement.scrollHeight > childElement.clientHeight)),
    );
    setOverflowed(nextOverflowed);
  }, []);

  useEffect(() => {
    updateOverflowState();
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      updateOverflowState();
    });
    observer.observe(node);
    const childNode = node.firstElementChild;
    if (childNode instanceof HTMLElement) {
      observer.observe(childNode);
    }
    return () => {
      observer.disconnect();
    };
  }, [resolvedTitle, updateOverflowState]);

  return (
    <Tooltip
      title={resolvedTitle}
      disableHoverListener={!resolvedTitle || !overflowed}
      placement={placement}
      arrow
      slotProps={{
        tooltip: {
          sx: {
            fontSize: '15px',
            whiteSpace: 'nowrap',
            width: 'fit-content',
            maxWidth: 'calc(100vw - 32px)',
          },
        },
      }}
    >
      <Box
        ref={containerRef}
        component="span"
        onMouseEnter={updateOverflowState}
        onFocus={updateOverflowState}
        sx={[
          {
            minWidth: 0,
            maxWidth: '100%',
            width: inline ? 'auto' : '100%',
            display: inline ? 'inline-block' : 'block',
          },
          ...(wrapperSx ? (Array.isArray(wrapperSx) ? wrapperSx : [wrapperSx]) : []),
        ]}
      >
        {children}
      </Box>
    </Tooltip>
  );
};
